import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import useAuth from "../../hooks/useAuth";
import useFetch from "../../hooks/useFetch";
import Pagination from "../../components/Table/pagination";

// PDF DOWNLOAD //
import jsPDF from "jspdf";
import "jspdf-autotable";

// EXCEL DOWNLOAD //
import * as XLSX from "xlsx";

import { ReactComponent as Pdf } from "../../assets/icons/pdf-file-svgrepo-com.svg";
import { ReactComponent as Excel } from "../../assets/icons/excel-svgrepo-com.svg";

// feedback_labels / feedback_questions come back as JSONField data from the
// backend (arrays or objects), not plain strings - render defensively so an
// unexpected shape shows something readable instead of crashing the table.
const renderJsonField = (value) => {
  if (value === null || value === undefined || value === "") return "-";

  if (Array.isArray(value)) {
    if (value.length === 0) return "-";
    return value
      .map((item) =>
        typeof item === "object" && item !== null
          ? Object.values(item).join(": ")
          : String(item)
      )
      .join(", ");
  }

  if (typeof value === "object") {
    const entries = Object.entries(value);
    if (entries.length === 0) return "-";
    return entries.map(([k, v]) => `${k}: ${v}`).join(", ");
  }

  return String(value);
};

const FollowUpReport = (props) => {
  const { auth } = useAuth();

  const Initial_pagination = {
    next: "",
    previous: "",
    count: "",
    total_pages: "",
    current_page: 1,
    page_size: 5,
  };

  const [FollowUpList, setFollowUpList] = useState([]);
  const [pagination, setPagination] = useState(Initial_pagination);
  const [loading, setLoading] = useState(false);

  const PaginationControl = (type, number) => {
    if (type == "page") {
      if (number <= pagination.total_pages) {
        fetchCampainCustomers(number);
      } else {
        fetchCampainCustomers(1);
      }
    } else if (type == "last") {
      fetchCampainCustomers(pagination.total_pages);
    } else if (type == "prev") {
      if (pagination.current_page > 1) {
        fetchCampainCustomers(pagination.current_page - 1);
      }
    } else if (type == "next") {
      if (pagination.current_page < pagination.total_pages) {
        fetchCampainCustomers(pagination.current_page + 1);
      }
    } else {
      fetchCampainCustomers(1);
    }
  };

  const fetchCampainCustomers = async (pagenumber) => {
    let postData = {
      user_id: auth?.user_details?.user,
      user_type: props.user_type,
      userType : auth.user_details.type,
      page: pagenumber,
      input_text: searchText,
      from_date: fromDate || "",
      to_date: toDate || "",
    };

    let config = {
      headers: { "Content-Type": "application/json" },
    };

    setLoading(true);

    try {
      const res = await axios.get(
        process.env.REACT_APP_API_URL +
          "/contactcentre/FollowUpDetailedReportContactcenter/",
        {
          ...config,
          params: postData,
        }
      );

      setFollowUpList(res.data.results || []);

      setPagination((prevState) => ({
        ...prevState,
        total_pages: res.data?.total_pages || 0,
        previous: res.data?.previous || null,
        next: res.data?.next || null,
        current_page: res.data?.current_page || 1,
        count: res.data?.count || 0,
        page_size: res.data?.page_size || 5,
      }));
    } catch (err) {
      console.error("Failed to fetch follow-up report:", err);
      Swal.fire({
        icon: "error",
        title: "Failed to load report",
        text:
          err?.response?.data?.message ||
          "Something went wrong while fetching the follow-up report. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllFollowUpData = async () => {
    const params = {
      user_id: auth?.user_details?.user,
      user_type: props.user_type,
      userType: auth?.user_details?.type,
      input_text: searchText,
      from_date: fromDate || "",
      to_date: toDate || "",
      download: true,
    };

    try {
      const res = await axios.get(
        process.env.REACT_APP_API_URL +
          "/contactcentre/FollowUpDetailedReportContactcenter/",
        {
          headers: { "Content-Type": "application/json" },
          params: params,
        }
      );

      return Array.isArray(res.data) ? res.data : res.data.results || [];
    } catch (error) {
      console.error("Failed to fetch all follow-up data:", error);

      Swal.fire({
        icon: "error",
        title: "Download failed",
        text: "Unable to fetch all follow-up records.",
      });

      return [];
    }
  };

  useEffect(() => {
    fetchCampainCustomers(1);
  }, []);

  const columnCount = 10;

  const ExpandableComment = ({ text, limit = 15 }) => {
    const [expanded, setExpanded] = useState(false);

    if (!text) return null;

    const isLong = text.length > limit;
    const displayText = expanded || !isLong ? text : `${text.slice(0, limit)}...`;

    return (
      <span>
        {displayText}
        {isLong && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-blue-500 ml-1 text-sm font-medium hover:underline"
          >
            {expanded ? "Show less" : "Read more"}
          </button>
        )}
      </span>
    );
  };

  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const FetchData = () => {
      fetchCampainCustomers(1);
  };

  // -------   PDF & EXCEL DOWNLOAD (added, same pattern as CampaignReport)  -------- //

  const downloadpdfData = (dataset) => {
    try {
      const doc = new jsPDF();
      doc.text("Follow Up Report", 10, 10);

      const tableData = dataset.flatMap((obj, index) => {
        return [
          [index < 9 ? `0${index + 1}` : `${index + 1}`, 'Customer', `${obj.customer_name || ""} (${obj.customer_number || ""})`],
          ['', 'Agent', `${obj.agent_name || ""} (${obj.agent_number || ""})`],
          ['', 'Campaign', obj.campaign_name],
          ['', 'FollowUp Date', obj.follow_up_date],
          ['', 'Comment', obj.comment],
          ['', 'Feedback Labels', renderJsonField(obj.feedback_labels)],
          ['', 'Feedback Questions', renderJsonField(obj.feedback_questions)],
          ['', '', ''], // Empty row for spacing between entries
        ];
      });

      doc.autoTable({
        head: [['S.No', 'Head', 'Data']],
        body: tableData,
        startY: 20,
        styles: {
          cellPadding: 1,
          fontSize: 10,
        },
        columnStyles: {
          0: { cellWidth: 20 },
          1: { cellWidth: 50 },
          2: { cellWidth: 110 },
        },
      });

      doc.save("FollowUpReport.pdf");
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const downloadxcelData = (dataset) => {
    const excelData = dataset.map((obj) => ({
      "Customer Name": obj.customer_name,
      "Customer Number": obj.customer_number,
      "Agent": obj.agent_name,
      "Agent Number": obj.agent_number,
      "Campaign Name": obj.campaign_name,
      "FollowUp Date": obj.follow_up_date,
      "Comment": obj.comment,
      "Feedback Labels": renderJsonField(obj.feedback_labels),
      "Feedback Questions": renderJsonField(obj.feedback_questions),
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(wb, ws, "Follow Up Report");
    const blobData = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const excelBlob = new Blob([blobData], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(excelBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "followup_report.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadSheet = async (type) => {
    try {
      const allData = await fetchAllFollowUpData();

      if (!allData.length) {
        Swal.fire({
          icon: "info",
          title: "No data",
          text: "No records available to download.",
        });
        return;
      }

      if (type === "PDF") {
        downloadpdfData(allData);
      } else {
        downloadxcelData(allData);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // -------   END PDF & EXCEL DOWNLOAD  -------- //

  return (
    <>
      <div className="flex flex-col bg-white p-4">

        <h1 className="font-bold text-lg ps-7">FollowUp Report</h1>
        <div className="flex justify-items-start gap-6 p-7 pt-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                className="w-5 h-5 text-gray-500"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                ></path>
              </svg>
            </div>
            <input
              type="text"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-80 pl-10 p-2.5"
              placeholder="Search for items"
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <span className="mx-2 text-gray-500 self-center">From</span>
          <input
            name="start"
            type="date"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5"
            onChange={(e) => setFromDate(e.target.value)}
          />

          <span className="mx-2 text-gray-500 self-center">To</span>
          <input
            name="end"
            type="date"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5"
            onChange={(e) => setToDate(e.target.value)}
          />


          {/* PDF & EXCEL DOWNLOAD (added, same pattern as CampaignReport) */}
          <div
            className="flex justify-between items-center space-x-3 bg-red-100 p-2 rounded-2xl cursor-pointer"
            onClick={() => downloadSheet("PDF")}
          >
            <Pdf className="w-5 h-5" />
            <h1 className="text-red-500">PDF</h1>
          </div>
          <div
            className="flex justify-between items-center space-x-3 bg-green-100 p-2 rounded-2xl cursor-pointer"
            onClick={() => downloadSheet("EXCEL")}
          >
            <Excel className="w-5 h-5" />
            <h1 className="text-green-500">Excel</h1>
          </div>
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            onClick={FetchData}
          >
            Submit
          </button>
        </div>

        <div className="py-2 inline-block min-w-full">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-[#9AA7B5] text-[17px] font-karla font-[500]">
                  <th className="w-full font-[500] pl-[2.1rem] pr-[1.5rem] text-start">
                    Sl-No
                  </th>
                  <th className="w-full font-[500] pl-[2.1rem] pr-[1.5rem] text-start">
                    Customer
                  </th>
                  <th className="w-full font-[500] pl-[2.1rem] pr-[1.5rem] text-start">
                    Agent
                  </th>
                  <th className="w-full font-[500] pl-[2.1rem] pr-[1.5rem] text-start">
                    Campaign Name
                  </th>
                  <th className="w-full font-[500] pl-[2.1rem] pr-[1.5rem] text-start">
                    FollowUp Date
                  </th>
                  <th className="w-full font-[500] pl-[2.1rem] pr-[1.5rem] text-start">
                    Comment
                  </th>
                  <th className="w-full font-[500] pl-[2.1rem] pr-[1.5rem] text-start">
                    Feedback Labels
                  </th>
                  <th className="w-full font-[500] pl-[2.1rem] pr-[1.5rem] text-start">
                    Feedback Questions
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr className="border-b text-[16px] font-karla text-gray-900 whitespace-nowrap">
                    <td
                      className="py-4 pl-[2.1rem] pr-[1.5rem] text-center"
                      colSpan={columnCount}
                    >
                      Loading...
                    </td>
                  </tr>
                ) : FollowUpList.length === 0 ? (
                  <tr className="border-b text-[16px] font-karla text-gray-900 whitespace-nowrap">
                    <td
                      className="py-4 pl-[2.1rem] pr-[1.5rem] text-center"
                      colSpan={columnCount}
                    >
                      No records found.
                    </td>
                  </tr>
                ) : (
                  FollowUpList.map((obj, index) => (
                    <tr
                      key={obj.id ?? `${obj.customer_number}-${index}`}
                      className="border-b text-[16px] font-karla text-gray-900 whitespace-nowrap"
                    >
                      <td className="py-4 pl-[2.1rem] pr-[1.5rem]">
                        {index +
                          1 +
                          (pagination?.current_page - 1) *
                            pagination?.page_size}
                      </td>
                      <td className="py-4 pl-[2.1rem] pr-[1.5rem]">
                        {obj.customer_name}<br></br>
                        {obj.customer_number}
                      </td>
                      <td className="py-4 pl-[2.1rem] pr-[1.5rem]">
                        {obj.agent_name}<br></br>
                        {obj.agent_number}
                      </td>
                      <td className="py-4 pl-[2.1rem] pr-[1.5rem]">
                        {obj.campaign_name}
                      </td>
                      <td className="py-4 pl-[2.1rem] pr-[1.5rem]">
                        {obj.follow_up_date}
                      </td>
                      <td className="py-4 pl-[2.1rem] pr-[1.5rem]">
                        <ExpandableComment text={obj.comment} />
                      </td>
                      <td className="py-4 pl-[2.1rem] pr-[1.5rem]">
                        <ExpandableComment text={renderJsonField(obj.feedback_labels)} />
                      </td>
                      <td className="py-4 pl-[2.1rem] pr-[1.5rem]">
                        <ExpandableComment text={renderJsonField(obj.feedback_questions)} />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {FollowUpList.length !== 0 ? (
          <div className="p-8">
            <Pagination
              pagination_data={pagination}
              PaginationControl={PaginationControl}
            />
          </div>
        ): <></>
        }
      </div>
    </>
  );
};

export default FollowUpReport;